<template>
  <div 
    v-show="(paquete === 2 || paquete === 3 || paquete === 4) && welcomeText"
    ref="bienvenidaRef"
    class="bienvenida-overlay"
  >
    <div ref="contenidoRef" class="overlay-content">
      <!-- Top banner with ornament and branch below -->
      <div class="franja-superior">
        <div class="decoracion-wrapper">
          <!-- BRANCH: Lower z-index and adjusted position -->
          <img ref="ramaRef" src="@/assets/rama.png" alt="branch" class="rama-decorativa" />
          <!-- FLOWER: In front -->
          <img ref="ornamentoArriba" src="@/assets/monio.png" alt="ornament" class="ornamento-arriba" />
        </div>
        <span ref="tituloRef" class="titulo-pase">Dear</span>
      </div>
      
      <!-- Guest Name -->
      <div ref="textoRef" class="welcome-text">
        {{ welcomeText }}
      </div>

      <!-- Welcome Message -->
      <div ref="mensajeRef" class="mensaje-bienvenida">
        It would be an honor to have your presence on this special day as I celebrate my Sweet 15.
        <br><br>
      </div>

      <!-- Info -->
      <div class="info-container">
        <div v-if="(paquete === 3 || paquete === 4) && numpase" ref="paseRef" class="info-item">
          <i class="fas fa-ticket-alt"></i>
          <span>PASSES: {{ numpase }}</span>
        </div>
        <div v-if="paquete === 4 && mesaText" ref="mesaRef" class="info-item">
          <i class="fas fa-circle"></i>
          <span>TABLE: {{ mesaText }}</span>
        </div>
      </div>
    </div>

    <div v-show="showTooltip" ref="tooltipRef" class="tooltip">
      Remember to confirm your attendance!
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, onBeforeUnmount } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import axios from 'axios';
import baseUrl from '@/api';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'InvitadoEvento',
  props: {
    idInvitado: Number,
    nombre: String,
    idFestejado: Number
  },
  setup(props) {
    const bienvenidaRef = ref(null);
    const contenidoRef = ref(null);
    const tituloRef = ref(null);
    const textoRef = ref(null);
    const mensajeRef = ref(null);
    const paseRef = ref(null);
    const mesaRef = ref(null);
    const tooltipRef = ref(null);
    const ornamentoArriba = ref(null);
    const ramaRef = ref(null);

    const welcomeText = ref('');
    const mesaText = ref('');
    const numpase = ref(null);
    const paquete = ref(null);
    const showTooltip = ref(false);
    let tooltipTimeout;

    const fetchData = async () => {
      if (!isNaN(props.idInvitado) && props.idInvitado > 0) {
        try {
          const url = `${baseUrl}/${props.nombre || ''}/${props.idFestejado || ''}/${props.idInvitado}`;
          const response = await axios.get(url);
          welcomeText.value = response.data.nombre_mostrar;
          mesaText.value = response.data.num_mesa;
          numpase.value = response.data.num_pase;
          paquete.value = response.data.paquete;
        } catch (err) {
          console.error('Error al obtener los datos', err);
        }
      }
    };

    const animateElements = () => {
      const tl = gsap.timeline();

      // Estado inicial: Rama escondida justo detrás del centro de la flor
      gsap.set(ramaRef.value, { scale: 0.2, opacity: 0, transformOrigin: "50% 20%" });

      // 1. Flor aparece girando
      tl.fromTo(ornamentoArriba.value, { rotation: 0, scale: 0, opacity: 0 }, {
        rotation: 360,
        scale: 1,
        opacity: 0.9,
        duration: 1.2,
        ease: 'power2.out'
      });

      // 2. Rama nace y "cae" suavemente hacia abajo/atrás
      tl.to(ramaRef.value, {
        scale: 1.1,
        opacity: 0.7,
        y: 10,
        duration: 1.8,
        ease: "power1.out"
      }, "-=0.6");

      tl.fromTo(tituloRef.value, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8 }, "-=1.2");
      tl.fromTo(textoRef.value, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8 }, "-=0.8");
      tl.fromTo(mensajeRef.value, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8 }, "-=0.8");
      tl.fromTo([paseRef.value, mesaRef.value], { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.8 }, "-=0.8");

      gsap.fromTo(tooltipRef.value, { opacity: 0, scale: 0.8, y: 10 }, { opacity: 1, scale: 1, y: 0, duration: 1.2, ease: "sine.inOut" });
    };

    const resetElements = () => {
      gsap.set([tituloRef.value, textoRef.value, mensajeRef.value, paseRef.value, mesaRef.value], { opacity: 0, y: 20 });
      gsap.set(ornamentoArriba.value, { rotation: 0, opacity: 0, scale: 0 });
      gsap.set(ramaRef.value, { scale: 0.2, opacity: 0 });
      gsap.set(tooltipRef.value, { opacity: 0, y: 10 });
    };

    onMounted(async () => {
      await fetchData();
      await nextTick();

      ScrollTrigger.create({
        trigger: bienvenidaRef.value,
        start: 'top 85%',
        onEnter: () => {
          showTooltip.value = true;
          animateElements();
        },
        onLeaveBack: () => {
          showTooltip.value = false;
          resetElements();
        }
      });
    });

    onBeforeUnmount(() => clearTimeout(tooltipTimeout));

    return {
      bienvenidaRef, contenidoRef, tituloRef, textoRef, mensajeRef,
      paseRef, mesaRef, tooltipRef, ornamentoArriba, ramaRef,
      welcomeText, mesaText, numpase, paquete, showTooltip
    };
  }
};
</script>

<style scoped>
.bienvenida-overlay {
  position: relative; /* Agregado para contener posicionamientos absolutos como el tooltip */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 50vh;
  padding: 10px 10px 40px 10px; /* Padding inferior para dar espacio al tooltip */
}

.overlay-content {
  padding: 30px;
  text-align: center;
  width: 450px;
  max-width: 100%;
  position: relative;
}

/* CONTENEDOR DECORATIVO */
.decoracion-wrapper {
  position: absolute;
  right: -40px;
  top: -10px;
  width: 120px;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* LA RAMA: Debajo de la flor y más etérea */
.rama-decorativa {
  position: absolute;
  width: 130%; 
  height: auto;
  z-index: 1; /* Atrás */
  top: 20px;
  filter: brightness(1.1) opacity(0.9);
}

/* LA FLOR: Al frente */
.ornamento-arriba {
  position: relative;
  width: 150px;
  height: auto;
  z-index: 2; /* Adelante */
}

.titulo-pase {
  font-family: 'CynthiaJuneJF', cursive;
  font-size: 56px;
background: linear-gradient(110deg, #e6a6c6 30%, #ff85ae 45%, #faadd8 50%, #ff85c2 55%, #ef90ae 70%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0px 2px 3px rgba(0,0,0,0.3));
  display: block; 
}

.welcome-text {
  font-size: 1.4rem;
  font-family: 'quote', serif;
  color: #1a1a1a;
  margin: 15px 0 10px;
}

.mensaje-bienvenida {
  font-size: 1rem;
  font-family: 'padres';
  color: #050505;
}

.info-container {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
}

.info-item {
  display: flex;
  align-items: center;
  color: #171717;
  font-family: 'padres';
}

/* TOOLTIP POSICIONADO CORRECTAMENTE DENTRO DEL VUE */
.tooltip {
  position: absolute;
  bottom: 5px; /* Ajustado para quedar flotando dentro del contenedor del componente */
  left: 50%;
  transform: translateX(-50%);
  background-image: url('@/assets/dorado.jpeg');
  background-size: cover;
  color: white;
  padding: 6px 18px;
  border-radius: 8px;
  font-size: 14px;
  opacity: 0;
  z-index: 10; /* Asegura quedar por encima de otros elementos */
  box-shadow: 0px 4px 10px rgba(0,0,0,0.2);
  white-space: nowrap;
}
</style>